import {ReactNode, useReducer, useState} from 'react';
import {
  AttendanceFilterGroup,
  DailyCareLogFilters,
  FiltersActionType,
  DailyCareLogFiltersState,
} from './types';
import reducer from './reducer';
import {DailyCareLogFiltersContext} from './context';
import {ActivitiesSector} from '../../backend/interfaces/activities-sector';
import {EntityActivitiesRepository} from '../../repositories/entity-activities';
import {API_PAGINATION_MAX_LIMIT} from '../../utils/constants/api';
import {ActivitiesGroup} from '../../backend/interfaces/activities-group';
import {AffiliatedUser} from '../../backend/responses/affiliated-user';
import {useEntity} from '../entity/context';
import {EntityOccurrenceTypeCategory} from '../../backend/responses/entity-occurrence';

const initialState: DailyCareLogFiltersState = {
  activitiesSectors: undefined,
  activityGroups: {},
  taskFilters: {
    activitiesGroupIds: [],
    socialResponseIds: [],
    users: [],
    status: [],
    occurrencesTypesGeneral: [],
    occurrencesTypesPatients: [],
    attendanceMovements: [],
    attendanceStatus: [],
    attendancePeriods: [],
  },
};

export const DailyCareLogFiltersContextProvider = ({
  children,
  users,
}: {
  children: ReactNode;
  users?: AffiliatedUser[];
}) => {
  const {
    state: {userEntitySocialResponses},
  } = useEntity();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [enabledActivitiesSectorIds, setEnabledActivitiesSectorIds] = useState<string[]>([]);

  // NOTE: ideally, we're reuse the useFetchAllItems here and below, but we can't really do that due to the fact that
  // sectors and groups are dynamic and vary across entities, so there's not a good way of dynamically loading them
  // just once and keeping track across the multiple tabs in which the filters are used. For now we'll keep the logic
  // for fetching all items replicated here.
  const fetchActivitiesSectors = async (entityId: string) => {
    const allSectors: ActivitiesSector[] = [];
    let totalCount;

    do {
      const response = await EntityActivitiesRepository.instance.getSectors(
        allSectors.length,
        API_PAGINATION_MAX_LIMIT,
        entityId
      );
      totalCount ||= response.totalCount;
      allSectors.push(...response.data);
    } while (allSectors.length < totalCount);

    // Set all sectors
    dispatch({
      type: FiltersActionType.SET_ACTIVITIES_SECTORS,
      activitiesSectors: allSectors,
    });
  };

  const fetchActivitiesGroups = async (entityId: string, activitiesSectorId: string) => {
    const sectorGroups: ActivitiesGroup[] = [];
    let totalCount;

    do {
      const response = await EntityActivitiesRepository.instance.getGroups(
        sectorGroups.length,
        API_PAGINATION_MAX_LIMIT,
        entityId,
        activitiesSectorId
      );
      totalCount ||= response.totalCount;
      sectorGroups.push(...response.data);
    } while (sectorGroups.length < totalCount);

    // Set sector activities groups
    dispatch({
      type: FiltersActionType.SET_ACTIVITIES_SECTOR_GROUPS,
      activitiesSectorId,
      activitiesGroups: sectorGroups,
    });

    // NOTE: when a sector is expanded by clicking on the checkbox we want to make all nested groups inherit the
    // checked value. However, when groups finish loading we have no context of how the groups were expanded, so we
    // need to keep track of the toggled sector ids internally to re-trigger the `TOGGLE_ACTIVITIES_SECTOR_FILTER`
    // action to make the group checkboxes inherit the right state.
    dispatch({
      type: FiltersActionType.TOGGLE_ACTIVITIES_SECTOR_FILTER,
      activitiesSectorId,
      selected: enabledActivitiesSectorIds.includes(activitiesSectorId),
    });
  };

  const setActiveFilters = (activeFilters: DailyCareLogFilters) =>
    dispatch({
      type: FiltersActionType.SET_TASKS_FILTERS,
      tasksFilters: activeFilters,
    });

  const toggleActivitiesSectorFilter = (activitiesSectorId: string, selected: boolean) => {
    setEnabledActivitiesSectorIds(
      selected
        ? [...enabledActivitiesSectorIds, activitiesSectorId]
        : enabledActivitiesSectorIds.filter((sectorId) => sectorId !== activitiesSectorId)
    );

    return dispatch({
      type: FiltersActionType.TOGGLE_ACTIVITIES_SECTOR_FILTER,
      activitiesSectorId,
      selected,
    });
  };

  const toggleActivitiesGroupFilter = (activitiesGroupId: string, selected: boolean) =>
    dispatch({
      type: FiltersActionType.TOGGLE_ACTIVITIES_GROUP_FILTER,
      activitiesGroupId,
      selected,
    });

  const toggleSocialResponseFilter = (socialResponseId: string, selected: boolean) =>
    dispatch({
      type: FiltersActionType.TOGGLE_SOCIAL_RESPONSE_FILTER,
      socialResponseId,
      selected,
    });

  const toggleAllSocialResponseFilters = (selected: boolean) =>
    dispatch({
      type: FiltersActionType.TOGGLE_ALL_SOCIAL_RESPONSE_FILTERS,
      selected,
      socialResponses: userEntitySocialResponses,
    });

  const toggleStatusFilter = (status: string, selected: boolean) => {
    dispatch({
      type: FiltersActionType.TOGGLE_STATUS_FILTER,
      status,
      selected,
    });
  };

  const toggleUserFilter = (userId: string, selected: boolean) => {
    dispatch({
      type: FiltersActionType.TOGGLE_USER_FILTER,
      userId,
      selected,
    });
  };

  const toggleOccurrenceTypeFilter = (
    occurrenceType: string,
    selected: boolean,
    category: EntityOccurrenceTypeCategory
  ) => {
    if (category === 'general') {
      dispatch({
        type: FiltersActionType.TOGGLE_OCCURRENCE_TYPE_GENERAL_FILTER,
        occurrenceType,
        selected,
      });
    } else {
      dispatch({
        type: FiltersActionType.TOGGLE_OCCURRENCE_TYPE_PATIENTS_FILTER,
        occurrenceType,
        selected,
      });
    }
  };

  const toggleAttendanceFilter = (group: AttendanceFilterGroup, value: string, selected: boolean) =>
    dispatch({type: FiltersActionType.TOGGLE_ATTENDANCE_FILTER, group, value, selected});

  const toggleAllAttendanceFilters = (group: AttendanceFilterGroup, values: string[]) =>
    dispatch({type: FiltersActionType.TOGGLE_ALL_ATTENDANCE_FILTERS, group, values});

  const clearAllFilters = () => dispatch({type: FiltersActionType.CLEAR_ALL_FILTERS});

  const toggleAllUserFilters = (selected: boolean, userIds: string[]) => {
    dispatch({
      type: FiltersActionType.TOGGLE_ALL_USER_FILTERS,
      selected,
      users: userIds,
    });
  };

  const toggleAllStatusFilters = (selected: boolean, status: string[]) => {
    dispatch({
      type: FiltersActionType.TOGGLE_ALL_STATUS_FILTERS,
      selected,
      status,
    });
  };

  const toggleAllOccurrenceTypeFilters = (
    selected: boolean,
    occurrenceTypes: string[],
    category: EntityOccurrenceTypeCategory
  ) => {
    if (category === 'general') {
      dispatch({
        type: FiltersActionType.TOGGLE_ALL_OCCURRENCE_TYPE_GENERAL_FILTERS,
        selected,
        occurrenceTypes,
        category,
      });
    } else {
      dispatch({
        type: FiltersActionType.TOGGLE_ALL_OCCURRENCE_TYPE_PATIENTS_FILTERS,
        selected,
        occurrenceTypes,
        category,
      });
    }
  };

  return (
    <DailyCareLogFiltersContext.Provider
      value={{
        // data
        activitiesSectors: state.activitiesSectors,
        activityGroups: state.activityGroups,
        users,
        // filters
        tasksFilters: state.taskFilters,

        // data loading
        fetchActivitiesSectors,
        fetchActivitiesGroups,

        // filters logic
        setTasksFilters: setActiveFilters,
        toggleActivitiesSectorFilter,
        toggleActivitiesGroupFilter,
        toggleSocialResponseFilter,
        toggleAllSocialResponseFilters,
        toggleStatusFilter,
        toggleUserFilter,
        toggleOccurrenceTypeFilter,
        toggleAllUserFilters,
        toggleAllStatusFilters,
        toggleAllOccurrenceTypeFilters,
        toggleAttendanceFilter,
        toggleAllAttendanceFilters,
        clearAllFilters,
      }}
    >
      {children}
    </DailyCareLogFiltersContext.Provider>
  );
};
